import { useEffect, useState } from 'react'
import { admin } from '../api/client'
import type {
  FailedPush, IntakeAlert, IntakeOverview, PushRefusal, ShadowComparison, StationAlert,
} from '../api/types'
import { AdminSection, AdminSections } from '../components/AdminSection'
import { PushLogins } from '../components/PushLogins'
import { Warnings } from '../components/Warnings'
import { alertText, checksStale, severity } from '../lib/health'
import { ROLE_LABEL, formatBytes, platformAlertText, queueLate, refusalText,
         shadowVerdict, workerState } from '../lib/intake'
import { formatDateTime, formatTime, relativeAge } from '../lib/time'
import { useAsync } from '../lib/useAsync'
import { useMutation } from '../lib/useMutation'
import { usePolled } from '../lib/usePolled'

const REFRESH_MS = 30_000
/** The health monitor's own disk threshold (DISK_ALERT_FRACTION). */
const DISK_ALERT = 0.8
/** What each worker runs (HANDBOOK, Collection): said under its name. */
const ROLE_TASKS: Record<string, string> = {
  fast: 'telemetry, live, names, push parsing',
  slow: 'daily, reconcile, audit, purge',
  health: 'health checks',
  alerts: 'alert rules, every minute',
  mail: 'alert email, through Microsoft Graph',
}
const SHADOW_ID = 'section-intake-shadow'

type PushTab = 'waiting' | 'failed' | 'refused'

/** The platform's own state (intake v2 phase 5; spec §5), laid out as the
 *  Intake board: four figures, then System Status, Workers and Pushes open,
 *  and the tools folded below them (redesign Ruling 4, spec open question
 *  5). */
export function Intake() {
  const overview = usePolled(() => admin.intake(), [], REFRESH_MS)
  const failed = usePolled(() => admin.failedPushes(), [], REFRESH_MS)
  const refusals = usePolled(() => admin.refusals(50), [], REFRESH_MS)
  const stations = useAsync(() => admin.stations(), [])
  const mutation = useMutation()
  const [lastAt, setLastAt] = useState<Date>()
  useEffect(() => { if (overview.data) setLastAt(new Date()) }, [overview.data])

  const o = overview.data
  const stale = checksStale(o?.checked_at ?? null)

  async function reparse(push: FailedPush) {
    if (await mutation.run(() => admin.reparsePush(push.id))) {
      failed.reload()
      overview.reload()
    }
  }

  // The head button opens the fold and brings it into view. Its own toggle
  // handler (AdminSection) keeps its state in step.
  const runShadow = () => {
    const fold = document.getElementById(SHADOW_ID) as HTMLDetailsElement | null
    if (!fold) return
    fold.open = true
    fold.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }

  return (
    <div className="page intake">
      {import.meta.env.VITE_DEMO_MODE === 'true' && <p className="notice" role="status">Demo intake: no live workers, devices or collection jobs are connected.</p>}
      <div className="page-head">
        <div>
          <h1 className="sr-only">Intake</h1>
          <p className="muted">
            The platform's own state. Refreshed every 30 s
            {lastAt ? <>, last at <span className="mono">{formatTime(lastAt)}</span></> : ''}.
            {' '}
            {o?.checked_at
              ? `Health checked ${relativeAge(o.checked_at)}.`
              : 'The health checks have not run yet.'}
          </p>
        </div>
        <button type="button" className="button" onClick={runShadow}>Run shadow comparison</button>
      </div>

      {o && stale && (
        <div className="notice station-alert status-stale" role="status">
          <p className="station-alert-head">The health checks are not running.</p>
          <p className="muted">They run in ingest-slow; the alerts below are not current.</p>
        </div>
      )}
      <Warnings warnings={mutation.warnings} error={mutation.error} onDismiss={mutation.clear} />
      {overview.error && <p className="error">{overview.error}</p>}

      {o && <Kpis o={o} />}

      <div className="intake-row">
        <section className="intake-card intake-alerts" aria-labelledby="intake-status">
          <div className="intake-card-head">
            <h2 id="intake-status">System Status</h2>
            {o && <span className="muted">{alertSummary(o.alerts)}</span>}
          </div>
          {o && !o.alerts.length && <p className="intake-empty muted">Nothing needs attention.</p>}
          {o && o.alerts.length > 0 && (
            <ul className="alert-list">
              {o.alerts.map((a) => <AlertItem key={a.id} alert={a} />)}
            </ul>
          )}
        </section>

        <section className="intake-card intake-workers" aria-labelledby="intake-workers">
          <div className="intake-card-head"><h2 id="intake-workers">Workers</h2></div>
          {o && <Workers o={o} />}
        </section>
      </div>

      <Pushes o={o} failed={failed.data} refusals={refusals.data}
              busy={mutation.busy} onReparse={(p) => void reparse(p)} />

      <AdminSections>
        <AdminSection id="intake-logins" title="Push Logins"
          note="Each MRL logger's own login to the push listener. A secret is shown once, when it is issued; only its hash is kept. To rotate, issue a new one, have it entered, then revoke the old.">
          <PushLogins stations={stations.data} />
        </AdminSection>

        <AdminSection id="intake-shadow" title="Shadow Comparison"
          note="An MDS station's own pushes against what MDS delivered, through its current channels. A read-only look: switch-to-push moves the station and proves its channels itself.">
          <ShadowCompare stations={(stations.data ?? []).filter((s) => s.vendor === 'sommer')} />
        </AdminSection>

        <AdminSection id="intake-email" title="Email"
          note="Alert email: whether it is set up, the last email sent, and the emails that failed (the newest 50). Rules and who is told are on the Alerts page.">
          <EmailState />
        </AdminSection>

        <AdminSection id="intake-refusals" title="Refused Pushes"
          count={refusals.data ? (refusals.data.length ? `${refusals.data.length} recent` : 'none') : undefined}
          note="A logger pushing under the wrong login, or for a station that is not active. The last 50, with the address each came from.">
          {refusals.data && !refusals.data.length && <p className="muted">None.</p>}
          {refusals.data && refusals.data.length > 0 && <RefusalTable rows={refusals.data} withAddress />}
        </AdminSection>
      </AdminSections>
    </div>
  )
}

/** "2 stations with no data, 5 quiet channels" */
function alertSummary(alerts: IntakeAlert[]): string {
  if (!alerts.length) return 'All clear'
  const silent = alerts.filter((a) => a.kind === 'silent').length
  const quiet = alerts.reduce((n, a) =>
    n + (a.kind === 'channels_quiet' ? (a as StationAlert & { kind: 'channels_quiet' }).detail.count : 0), 0)
  const parts = [
    `${silent} ${silent === 1 ? 'station' : 'stations'} with no data`,
    quiet ? `${quiet} quiet ${quiet === 1 ? 'channel' : 'channels'}` : '',
  ].filter(Boolean)
  const rest = alerts.length - silent - alerts.filter((a) => a.kind === 'channels_quiet').length
  if (rest) parts.push(`${rest} other ${rest === 1 ? 'alert' : 'alerts'}`)
  return parts.join(', ')
}

function Kpi({ label, value, pill, tone, bar }: {
  label: string; value: string; pill: string; tone: 'normal' | 'warning' | 'alarm'
  bar?: number
}) {
  return (
    <div className="summary-card kpi">
      <span className="kpi-label">{label}</span>
      <span className="kpi-value">{value}</span>
      <span className={`pill status-${tone}`}>{pill}</span>
      {bar !== undefined && (
        <span className="kpi-bar" aria-hidden="true">
          <span style={{ width: `${Math.round(Math.min(Math.max(bar, 0), 1) * 100)}%` }} />
        </span>
      )}
    </div>
  )
}

/** Four figures from what the page already loads. A bar only where there
 *  is a whole to be a share of: workers reporting, and the disk. */
function Kpis({ o }: { o: IntakeOverview }) {
  const running = o.workers.filter((w) => workerState(w.beat_at) === 'running').length
  const stopped = o.workers.length - running
  const due = o.queue.reduce((n, q) => n + q.due, 0)
  const overdue = o.queue.filter((q) => queueLate(q)).length
  const used = o.disk && o.disk.total_bytes ? 1 - o.disk.free_bytes / o.disk.total_bytes : null
  return (
    <div className="summary-cards kpis">
      <Kpi label="Workers reporting" value={`${running} of ${o.workers.length}`}
           pill={!o.workers.length ? 'none registered' : stopped ? `${stopped} stopped` : 'all reporting'}
           tone={stopped || !o.workers.length ? 'alarm' : 'normal'}
           bar={o.workers.length ? running / o.workers.length : 0} />
      <Kpi label="Tasks due now" value={String(due)}
           pill={overdue ? `${overdue} overdue` : 'none overdue'}
           tone={overdue ? 'warning' : 'normal'} />
      <Kpi label="Pushes waiting" value={String(o.pushes.pending)}
           pill={o.pushes.failed ? `${o.pushes.failed} failed` : 'none failed'}
           tone={o.pushes.failed ? 'alarm' : 'normal'} />
      <Kpi label="Database" value={o.db_bytes ? formatBytes(o.db_bytes) : '—'}
           pill={used === null ? 'disk not measured' : `disk ${Math.round(used * 100)}% used`}
           tone={used !== null && used >= DISK_ALERT ? 'alarm' : 'normal'}
           bar={used ?? undefined} />
    </div>
  )
}

function AlertItem({ alert }: { alert: IntakeAlert }) {
  // A station's alert has a station; the platform's three kinds do not.
  const isStation = alert.station_code !== null
  const where = alert.station_name ?? (isStation ? alert.station_code ?? '' : 'Platform')
  const text = isStation
    ? alertText(alert.station_name ?? alert.station_code ?? '', alert as StationAlert).headline
    : platformAlertText(alert)
  const level = isStation ? severity(alert as StationAlert)
    : alert.kind === 'queue_lag' ? 'warning' : 'alarm'
  // A station's sentence begins with its name, which is set in bold rather
  // than said twice.
  const rest = text.startsWith(where) ? text.slice(where.length) : ` ${text}`
  return (
    <li className="alert-item">
      <span className={`pill status-${level === 'info' ? 'stale' : level}`}>
        {level === 'info' ? 'information' : level}
      </span>
      <span className="alert-text"><strong>{where}</strong>{rest}</span>
      <span className="mono alert-since" title={relativeAge(alert.opened_at)}>
        {formatDateTime(alert.opened_at)}
      </span>
    </li>
  )
}

function Workers({ o }: { o: IntakeOverview }) {
  return (
    <>
      <table className="grid">
        <thead><tr><th>Worker</th><th>Started</th><th>Last report</th><th /></tr></thead>
        <tbody>
          {!o.workers.length && <tr><td colSpan={4} className="muted">No worker has reported.</td></tr>}
          {o.workers.map((w) => {
            const state = workerState(w.beat_at)
            return (
              <tr key={w.worker_id}>
                <td>
                  <span className="mono">{ROLE_LABEL[w.role] ?? w.role}</span>{' '}
                  <code className="muted">{w.worker_id}</code>
                  {ROLE_TASKS[w.role] && <small className="worker-tasks muted">{ROLE_TASKS[w.role]}</small>}
                </td>
                <td className="mono">{formatDateTime(w.started_at)}</td>
                <td>{relativeAge(w.beat_at)}</td>
                <td>
                  <span className={`pill status-${state === 'running' ? 'normal' : 'alarm'}`}>
                    {state}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <table className="grid">
        <thead><tr><th>Task</th><th className="num">Due now</th><th>Oldest due</th><th /></tr></thead>
        <tbody>
          {!o.queue.length && <tr><td colSpan={4} className="muted">Nothing waiting.</td></tr>}
          {o.queue.map((q) => (
            <tr key={q.task}>
              <td>{q.task}</td><td className="num">{q.due}</td><td>{relativeAge(q.oldest_due_at)}</td>
              <td>{queueLate(q) && <span className="pill status-warning">overdue</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

function Pushes({ o, failed, refusals, busy, onReparse }: {
  o: IntakeOverview | undefined
  failed: FailedPush[] | undefined
  refusals: PushRefusal[] | undefined
  busy: boolean
  onReparse: (push: FailedPush) => void
}) {
  const [tab, setTab] = useState<PushTab>('failed')
  const counts: Record<PushTab, number | undefined> = {
    waiting: o?.pushes.pending, failed: o?.pushes.failed, refused: refusals?.length,
  }
  const label: Record<PushTab, string> = { waiting: 'Waiting', failed: 'Failed', refused: 'Refused' }
  return (
    <section className="intake-card" aria-labelledby="intake-pushes">
      <div className="intake-card-head">
        <h2 id="intake-pushes">Pushes</h2>
        <div className="segmented" role="group" aria-label="Push state">
          {(['waiting', 'failed', 'refused'] as PushTab[]).map((t) => (
            <button key={t} type="button" aria-pressed={tab === t} onClick={() => setTab(t)}>
              {label[t]} {counts[t] !== undefined && <span className="count">({counts[t]})</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="intake-card-body">
        {tab === 'waiting' && o && (
          <p className="muted">
            {o.pushes.pending
              ? `${o.pushes.pending} ${o.pushes.pending === 1 ? 'push is' : 'pushes are'} waiting to be parsed`
                + (o.pushes.oldest_pending_at
                  ? `; the oldest arrived ${formatDateTime(o.pushes.oldest_pending_at)} (${relativeAge(o.pushes.oldest_pending_at)}).`
                  : '.')
              : 'No push is waiting to be parsed.'}
          </p>
        )}
        {tab === 'failed' && (
          <>
            <p className="muted section-note">
              A failed push was acknowledged to the logger and could not be read; it is
              kept until re-parsed.
            </p>
            {failed && !failed.length && <p className="muted">None.</p>}
            {failed && failed.length > 0 && (
              <div className="live-scroll">
                <table className="grid">
                  <thead><tr><th>Push</th><th>Station</th><th>Arrived</th><th>Error</th><th /></tr></thead>
                  <tbody>
                    {failed.map((p) => (
                      <tr key={p.id}>
                        <td className="mono">{p.id}</td><td>{p.station}</td>
                        <td className="mono">{formatDateTime(p.received_at)}</td>
                        <td>{p.error}</td>
                        <td>
                          <button type="button" className="button" disabled={busy}
                                  onClick={() => onReparse(p)}>Re-parse</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
        {tab === 'refused' && (
          <>
            {refusals && !refusals.length && <p className="muted">None.</p>}
            {refusals && refusals.length > 0 && (
              <>
                <RefusalTable rows={refusals.slice(0, 10)} />
                {refusals.length > 10 && (
                  <p className="muted">The newest 10 of {refusals.length}; Refused Pushes below has them all.</p>
                )}
              </>
            )}
          </>
        )}
      </div>
    </section>
  )
}

function RefusalTable({ rows, withAddress = false }: { rows: PushRefusal[]; withAddress?: boolean }) {
  return (
    <div className="live-scroll">
      <table className="grid">
        <thead>
          <tr><th>When</th><th>Station</th><th>What</th>{withAddress && <th>From</th>}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="mono">{formatDateTime(r.at)}</td><td>{r.station ?? '—'}</td>
              <td>{refusalText(r)}</td>
              {withAddress && <td><code>{r.remote_addr ?? ''}</code></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ShadowCompare({ stations }: { stations: { code: string; name: string }[] }) {
  const [code, setCode] = useState('')
  const [result, setResult] = useState<ShadowComparison>()
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)

  async function compare() {
    setBusy(true)
    setError(undefined)
    setResult(undefined)
    try {
      setResult(await admin.shadow(code, 7))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="shadow-controls">
        <label className="field inline">
          <span>Station</span>
          <select value={code} onChange={(e) => setCode(e.target.value)}>
            <option value="">Choose an MDS station</option>
            {stations.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </label>
        <button type="button" className="button primary" disabled={!code || busy}
                onClick={() => void compare()}>
          {busy ? 'Comparing…' : 'Compare the last 7 days'}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
      {result && (
        <>
          <p><span className={`pill status-${result.passes ? 'normal' : 'warning'}`}>
            {shadowVerdict(result)}
          </span></p>
          {result.differences.length > 0 && (
            <table className="grid">
              <thead><tr><th>Parameter</th><th>Time</th><th className="num">Stored (MDS)</th><th className="num">Pushed</th></tr></thead>
              <tbody>
                {result.differences.map((d) => (
                  <tr key={`${d.parameter}-${d.ts}`}>
                    <td>{d.parameter}</td><td className="mono">{formatDateTime(d.ts)}</td>
                    <td className="num">{d.stored ?? '—'}</td><td className="num">{d.pushed ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  )
}

/** Alert email's own state (phase 4): set up or not, the last send, and
 *  the newest failures with why. */
function EmailState() {
  const status = useAsync(() => admin.mailStatus(), [])
  const failures = useAsync(() => admin.outbox('failed'), [])
  if (status.error) return <p className="error">{status.error}</p>
  if (!status.data) return <p className="muted">Loading…</p>
  const s = status.data
  return (
    <>
      <p>
        {s.configured ? <>Sending as <span className="mono">{s.sender}</span>.</>
          : 'Email is not set up yet: emails are recorded as not sent (docs/DEPLOYMENT.md, 10g).'}
        {' '}Last sent: {s.last_sent_at ? formatDateTime(s.last_sent_at) : 'never'}.
        {' '}Failed in the last 24 h: {s.failed_24h}.
      </p>
      {failures.data && failures.data.emails.length > 0 && (
        <table className="grid">
          <thead><tr><th>When</th><th>To</th><th>Subject</th><th>Why</th></tr></thead>
          <tbody>
            {failures.data.emails.map((m) => (
              <tr key={m.id}>
                <td className="mono">{formatDateTime(m.created_at)}</td>
                <td>{m.to_email}</td><td>{m.subject}</td><td>{m.last_error}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}
